import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { FormField } from '@/shared/components/custom/form-field/FormField';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { FormFieldTextarea } from '@/shared/components/custom/form-field/FormFieldTextarea';
import { PillRadioGroup } from '@/shared/components/custom/pill-radio-group';
import { useCreateMemberAdjustment } from '../../hooks/useCreateMemberAdjustment';
import {
  memberAdjustmentSchema,
  ADJUSTMENT_TYPE_OPTIONS,
  type MemberAdjustmentFormInput,
  type MemberAdjustmentFormValues,
} from '../../schemas';

interface MemberAdjustmentDialogProps {
  userId: string;
  displayName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Applies to the member's currently open cycle only — `NoOpenCycle` otherwise.
 * The Figma mockup also shows an "absence type" selector, but
 * `CreateMemberAdjustmentPayload` (`POST /time-off/members/{userId}/adjustments`)
 * has no `typeId` field at all — omitted rather than shown non-functional.
 *
 * @remarks
 * Open state is controlled by the caller (unlike a self-contained
 * `DialogTrigger`) so it can be launched from a `DropdownMenuItem`, which
 * can't nest its own dialog trigger.
 */
export function MemberAdjustmentDialog({
  userId,
  displayName,
  open,
  onOpenChange,
}: MemberAdjustmentDialogProps) {
  const { t } = useTranslation('timeOff');
  const createAdjustment = useCreateMemberAdjustment();
  const name = displayName || userId;

  const { control, handleSubmit, reset } = useForm<
    MemberAdjustmentFormInput,
    unknown,
    MemberAdjustmentFormValues
  >({
    resolver: zodResolver(memberAdjustmentSchema(t)),
    defaultValues: { adjustmentType: 'add', amount: 1, reason: '' },
  });

  function handleCancel() {
    reset();
    onOpenChange(false);
  }

  function onSubmit(values: MemberAdjustmentFormValues) {
    const days = values.adjustmentType === 'subtract' ? -values.amount : values.amount;
    createAdjustment.mutate(
      { userId, payload: { days, reason: values.reason } },
      {
        onSuccess: () => {
          reset();
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="time-off-member-adjustment-dialog">
        <DialogHeader>
          <DialogTitle>{t('team.members.adjustmentDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('team.members.adjustmentDialog.description', { name })}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-3"
          data-testid="time-off-member-adjustment-form"
        >
          <FormField
            control={control}
            name="adjustmentType"
            label={t('team.members.adjustmentDialog.adjustmentType')}
            render={({ field, invalid, disabled }) => (
              <PillRadioGroup
                id="adjustmentType"
                value={field.value as string}
                onValueChange={field.onChange}
                options={ADJUSTMENT_TYPE_OPTIONS.map((value) => ({
                  value,
                  label: t(`team.members.adjustmentDialog.types.${value}`),
                }))}
                disabled={disabled}
                aria-invalid={invalid}
                data-testid="time-off-member-adjustment-type"
              />
            )}
          />
          <FormFieldInput
            control={control}
            name="amount"
            type="number"
            label={t('team.members.adjustmentDays')}
            data-testid="time-off-member-adjustment-days"
          />
          <FormFieldTextarea
            control={control}
            name="reason"
            label={t('team.members.adjustmentReason')}
            placeholder={t('team.members.adjustmentDialog.reasonPlaceholder')}
            data-testid="time-off-member-adjustment-reason"
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCancel}>
              {t('team.members.adjustmentDialog.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={createAdjustment.isPending}
              data-testid="time-off-member-adjustment-submit"
            >
              {t('team.members.adjustmentDialog.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
